import React, { useState } from 'react';
import { X, Building2, Key, Mail, Tag, AlertCircle, CheckCircle } from 'lucide-react';
import { colors } from '../styles/colors';
import { useBankConnections } from '../hooks/bank/useBankConnections';

/**
 * Modal de configuration d'une connexion Qonto
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal ouverte ou fermée
 * @param {Function} props.onClose - Callback pour fermer la modal
 * @param {Function} props.onSuccess - Callback appelé après création réussie
 */
function QontoSetup({ isOpen, onClose, onSuccess }) {
  const { createConnection } = useBankConnections();

  const [connectionName, setConnectionName] = useState('');
  const [login, setLogin] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validation
    if (!login || !secretKey) {
      setError('Tous les champs sont requis');
      return;
    }

    if (login.trim().length < 3) {
      setError('L\'identifiant Qonto est trop court');
      return;
    }

    if (secretKey.length < 20) {
      setError('La clé secrète est trop courte (minimum 20 caractères)');
      return;
    }

    setLoading(true);

    // Debug logging
    console.log('[QontoSetup] Creating connection with:');
    console.log('- Login:', login);
    console.log('- Secret key length:', secretKey.length);
    console.log('- Secret key first 10:', secretKey.substring(0, 10));

    try {
      const result = await createConnection({
        bank_name: 'Qonto',
        connection_type: 'api',
        connection_name: connectionName.trim() || undefined,
        credentials: {
          login,
          secretKey
        }
      });

      console.log('Connexion Qonto créée:', result);

      setSuccess(true);
      setConnectionName('');
      setLogin('');
      setSecretKey('');

      // Attendre 2 secondes avant de fermer
      setTimeout(() => {
        onSuccess && onSuccess(result);
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Erreur lors de la création de la connexion Qonto:', err);
      setError(err.message || 'Erreur lors de la connexion à Qonto');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setConnectionName('');
      setLogin('');
      setSecretKey('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: colors.background.white,
        borderRadius: '0.75rem',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.light
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Building2 size={24} color={colors.primary.teal} />
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: colors.text.primary,
              margin: 0
            }}>
              Configurer Qonto
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              borderRadius: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.5 : 1
            }}
          >
            <X size={20} color={colors.text.secondary} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '1.5rem' }}>
            {/* Instructions */}
            <div style={{
              backgroundColor: colors.background.light,
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem',
              borderLeft: `4px solid ${colors.primary.teal}`
            }}>
              <p style={{
                fontSize: '0.875rem',
                color: colors.text.secondary,
                margin: 0,
                lineHeight: '1.5'
              }}>
                Pour obtenir vos identifiants API Qonto, rendez-vous dans{' '}
                <strong>Paramètres → Intégrations et API → Clés API</strong> de votre compte Qonto.
              </p>
            </div>

            {/* Connection Name Field */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.text.primary,
                marginBottom: '0.5rem'
              }}>
                <Tag size={16} color={colors.text.secondary} />
                Nom de la connexion <span style={{ color: colors.text.secondary, fontWeight: 400 }}>(optionnel)</span>
              </label>
              <input
                type="text"
                value={connectionName}
                onChange={(e) => setConnectionName(e.target.value)}
                placeholder="Ex : Compte principal, Compte formation..."
                disabled={loading || success}
                maxLength={100}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border.medium}`,
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: (loading || success) ? colors.background.light : colors.background.white
                }}
                onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                onBlur={(e) => e.target.style.borderColor = colors.border.medium}
              />
            </div>

            {/* Email/Login Field */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.text.primary,
                marginBottom: '0.5rem'
              }}>
                <Mail size={16} color={colors.text.secondary} />
                Identifiant Qonto (Login)
              </label>
              <input
                type="text"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="votre-identifiant"
                disabled={loading || success}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border.medium}`,
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: (loading || success) ? colors.background.light : colors.background.white
                }}
                onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                onBlur={(e) => e.target.style.borderColor = colors.border.medium}
              />
            </div>

            {/* Secret Key Field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: colors.text.primary,
                marginBottom: '0.5rem'
              }}>
                <Key size={16} color={colors.text.secondary} />
                Clé secrète API
              </label>
              <input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Votre clé secrète Qonto"
                disabled={loading || success}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border.medium}`,
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  fontFamily: 'monospace',
                  backgroundColor: (loading || success) ? colors.background.light : colors.background.white
                }}
                onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
                onBlur={(e) => e.target.style.borderColor = colors.border.medium}
              />
              <p style={{
                fontSize: '0.75rem',
                color: colors.text.secondary,
                marginTop: '0.5rem',
                marginBottom: 0
              }}>
                Cette clé sera chiffrée avant d'être stockée (AES-256-GCM)
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                <AlertCircle size={20} color="#DC2626" />
                <p style={{
                  fontSize: '0.875rem',
                  color: '#DC2626',
                  margin: 0
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#F0FDF4',
                border: '1px solid #86EFAC',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                <CheckCircle size={20} color="#16A34A" />
                <p style={{
                  fontSize: '0.875rem',
                  color: '#16A34A',
                  margin: 0
                }}>
                  Connexion Qonto créée avec succès !
                </p>
              </div>
            )}

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: `1px solid ${colors.border.medium}`,
                  backgroundColor: colors.background.white,
                  color: colors.text.primary,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = colors.background.light;
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.background.white;
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || success}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  backgroundColor: (loading || success) ? colors.border.medium : colors.primary.teal,
                  color: colors.background.white,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: (loading || success) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!loading && !success) {
                    e.target.style.backgroundColor = '#0D9488';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && !success) {
                    e.target.style.backgroundColor = colors.primary.teal;
                  }
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTopColor: colors.background.white,
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></span>
                    Connexion...
                  </>
                ) : success ? (
                  <>
                    <CheckCircle size={16} />
                    Connecté !
                  </>
                ) : (
                  'Connecter Qonto'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Animation CSS pour le spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default QontoSetup;
